import { useEffect } from "react";
import { Sparkles, Wrench, ArrowUpCircle, BookOpen, ExternalLink } from "lucide-react";
import { PUBLISHED, markSeen } from "@/lib/changelog";
import { PILOT_GUIDE_URL } from "@/lib/links";
import { useAppVersion } from "@/lib/electron";

const KIND = {
  new:      { label: "New",      icon: Sparkles,      cls: "text-primary" },
  improved: { label: "Improved", icon: ArrowUpCircle, cls: "text-sky-500" },
  fixed:    { label: "Fixed",    icon: Wrench,        cls: "text-emerald-500" },
};

const fmtDate = (d) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

export default function WhatsNew() {
  // "0.9.8 beta" in the desktop app, null in a browser.
  const running = useAppVersion()?.split(" ")[0] || null;

  // Opening the page is reading it: clears the sidebar dot.
  useEffect(() => { markSeen(); }, []);

  return (
    <div className="p-8 max-w-3xl" data-testid="whats-new-page">
      <div className="mb-6">
        <div className="overline mb-1">Releases</div>
        <h1 className="text-3xl font-bold tracking-tight">What's new</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          What changed in each version of VACARS5, newest first. The desktop app updates itself; this
          list comes with each update.
        </p>
        <a
          href={PILOT_GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whats-new-guide"
          className="inline-flex items-center gap-2 mt-4 text-xs font-mono uppercase tracking-wider border border-border rounded-md px-3 py-2 hover:border-primary hover:text-primary transition-colors"
        >
          <BookOpen className="w-4 h-4" /> Pilot guide (PDF) <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="space-y-4">
        {PUBLISHED.map((r) => (
          <section key={r.version} className="card-flat p-5" data-testid={`release-${r.version}`}>
            <div className="flex items-baseline gap-3 mb-3 flex-wrap">
              <h2 className="text-lg font-bold font-mono">v{r.version}</h2>
              <span className="text-xs text-muted-foreground">{fmtDate(r.date)}</span>
              {running === r.version && (
                <span
                  className="text-[10px] font-mono uppercase tracking-widest border border-primary text-primary rounded px-1.5 py-0.5"
                  data-testid="release-running"
                >
                  You're on this version
                </span>
              )}
            </div>
            <ul className="space-y-2">
              {r.items.map(([kind, text], i) => {
                const k = KIND[kind] || KIND.improved;
                return (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className={`shrink-0 w-24 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider ${k.cls}`}>
                      <k.icon className="w-3.5 h-3.5" /> {k.label}
                    </span>
                    <span className="leading-relaxed">{text}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
